import { mapSipStatusCode } from 'Modules/softphone/resolver';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { EventService } from 'Modules/softphone/services/EventService';
import { SipService } from 'Modules/softphone/services/SipService';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createOutboundCallSession } from 'Modules/softphone/utils/callFactories';
import { createLogger } from 'Modules/softphone/utils/logger';
import type { SessionState } from 'sip.js';

const logger = createLogger('CallManager:OutboundCallHandler');

export class OutboundCallHandler {
  /**
   * Make an outbound call
   * @param phoneNumber - Phone number to call (for external calls)
   * @param personId - Optional personId for internal calls
   * @param onSessionStateChange - Callback for session state changes
   * @returns Cleanup function to remove session state listener
   */
  async makeCall(
    phoneNumber: string,
    personId: string | undefined,
    onSessionStateChange: (callId: string, state: SessionState) => void
  ): Promise<() => void> {
    const sipService = SipService.getInstance();
    const webrtcService = WebRTCService.getInstance();
    const store = useSoftphoneStore.getState();

    const isInternalCall = !!personId;
    const callTarget = personId || phoneNumber;

    logger.info(
      `Making call: ${
        isInternalCall
          ? `internal (personId: ${personId})`
          : `external (phone: ${phoneNumber})`
      }`,
      {
        phoneNumber,
        personId,
        callTarget,
        hasUserAgent: !!sipService.getUserAgent(),
        hasConfig: !!sipService.getConfig(),
        sipStatus: store.status,
      }
    );

    // Check if SIP is initialized and registered
    if (!sipService.getUserAgent() || !sipService.getConfig()) {
      logger.error('SIP service not initialized', {
        hasUserAgent: !!sipService.getUserAgent(),
        hasConfig: !!sipService.getConfig(),
        status: store.status,
      });
      throw new Error(
        'Cannot make call: Softphone is not connected. Please wait for connection or check your settings.'
      );
    }

    if (store.status !== 'registered') {
      logger.error('SIP not registered', {
        status: store.status,
      });
      throw new Error(
        `Cannot make call: Softphone is ${store.status}. Please wait for connection.`
      );
    }

    // Create SIP inviter
    const inviter = sipService.makeCall(callTarget, personId);
    if (!inviter) {
      const sipConfig = sipService.getConfig();
      const userAgent = sipService.getUserAgent();

      logger.error('Failed to create inviter', {
        hasUserAgent: !!userAgent,
        hasConfig: !!sipConfig,
        callTarget,
        personId,
        isInternal: isInternalCall,
      });

      if (!userAgent || !sipConfig) {
        throw new Error(
          'SIP service not initialized. Please check your connection.'
        );
      }

      throw new Error('Failed to create call - invalid target');
    }

    // Create call session using factory
    const callSession = createOutboundCallSession(
      inviter,
      phoneNumber,
      personId
    );

    // Update store
    store.addCall(callSession);
    store.setCurrentCall(inviter.id);

    // Setup session state change handler
    const stateChangeHandler = (state: SessionState) => {
      onSessionStateChange(inviter.id, state);
    };

    inviter.stateChange.addListener(stateChangeHandler);

    // Create invite options with callbacks and codec enforcement
    const inviteOptions = sipService.createInviteOptions(
      (response) => {
        const answeredBy = response.message.getHeader('Answered-By');
        logger.info('Call ACCEPTED by remote party', { answeredBy });

        if (answeredBy === 'VmBox') {
          logger.info('Call answered by voicemail', { callId: inviter.id });
          useSoftphoneStore.getState().updateCall(inviter.id, {
            answeredByVoicemail: true,
          });
        }
      },
      (reason) => {
        const statusCode = reason?.message?.statusCode;
        const errorState = statusCode ? mapSipStatusCode(statusCode) : null;

        if (errorState) {
          useSoftphoneStore.getState().updateCall(inviter.id, {
            state: errorState,
            endTime: new Date().toISOString(),
          });
        }

        if (statusCode === 500) {
          logger.error(
            'Call rejected with 500 - Server Error. Target user may not be registered.',
            {
              statusCode,
              target: callTarget,
              isInternal: isInternalCall,
            }
          );
        } else {
          logger.debug('Call rejected', {
            statusCode,
            mappedState: errorState,
          });
        }
      },
      (response) => {
        const statusCode = response?.message?.statusCode;
        const audioService = AudioService.getInstance();

        if (statusCode === 180) {
          logger.info('Call RINGING at destination (180)');

          // Transition call state to 'ringing' so the ringback subscription
          // (which only triggers on 'connecting') stops managing audio.
          // This prevents overlapping audio when early media was playing.
          useSoftphoneStore.getState().updateCall(inviter.id, {
            state: 'ringing',
          });

          // Disconnect any early media (183) remote stream to prevent
          // connection audio from continuing in the background
          audioService.setRemoteStream(null);

          // Ensure ringback is playing at full volume for 180 Ringing
          if (!audioService.isPlaying('ringback')) {
            void audioService.play('ringback');
          }
          audioService.setVolume('ringback', 1.0);
          logger.debug('Ringback volume set to 100% for 180 Ringing');
        } else if (statusCode === 183) {
          logger.info('Call progress with early media (183)');

          // Setup remote media for early audio
          const call = useSoftphoneStore.getState().activeCalls.get(inviter.id);
          if (call?.session) {
            webrtcService.setupPeerConnection(call.session, inviter.id);
            const remoteStream = webrtcService.getRemoteStream(inviter.id);
            if (remoteStream && remoteStream.getTracks().length > 0) {
              audioService.setRemoteStream(remoteStream);
              // Reduce ringback volume when early media is available
              audioService.setVolume('ringback', 0.05);
              logger.debug(
                'Early media stream connected, ringback volume reduced to 5%'
              );
            } else {
              logger.debug(
                'No early media tracks available yet, keeping ringback at current volume'
              );
            }
          }
        } else {
          logger.debug('Call progress', { statusCode });
          // Stop ringback for unexpected provisional responses (matches legacy behavior)
          if (audioService.isPlaying('ringback')) {
            void audioService.stop('ringback');
          }
        }
      },
      {
        phoneNumber: callTarget,
        enforcePcmu: !isInternalCall,
        microphoneId: store.selectedMicrophone ?? undefined,
      }
    );

    // Send INVITE. No event is emitted for it, nor for the 180/183 responses
    // below: all three are traced off the socket by SipService, so a row here
    // would only repeat the message with a different wording. What the app
    // decided — the target, whether it is internal — is on the call record.
    EventService.getInstance().logCallEvent('Placing call', {
      callId: inviter.id,
      target: callTarget,
      phoneNumber,
      personId,
      isInternal: isInternalCall,
    });
    await inviter.invite(inviteOptions);
    logger.info('INVITE sent successfully');

    // Setup peer connection early so mute works during connecting/ringing states
    webrtcService.setupPeerConnection(inviter, inviter.id);

    logger.info('Outbound call initiated successfully', {
      callId: inviter.id,
      callTarget,
    });

    // Return the state change handler cleanup function
    return () => {
      inviter.stateChange.removeListener(stateChangeHandler);
    };
  }
}
